import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '../components/Button.js';
import { ArrowRightIcon } from '../components/icons/ArrowRightIcon.js';
import { WarningIcon } from '../components/icons/WarningIcon.js';
import { useAuth } from './AuthContext.js';

/** Fixed user-facing copy. The caught error is NEVER rendered — `generatePkce()` failures read
 *  like "crypto.subtle is undefined", and this string is the focal text of a branded card. */
const SIGN_IN_ERROR = "We couldn't start sign-in. Please try again.";
const IDLE_LABEL = 'Sign in';
const PENDING_LABEL = 'Signing in…';

/**
 * How long to stay latched before assuming the redirect is never going to happen.
 *
 * `window.location.assign()` neither throws nor rejects when the navigation itself fails, so an
 * offline user, a captive portal, or a blocked top-level navigation leaves `signIn()` resolved,
 * no catch fired, and the latch set forever — and because `.btn[aria-disabled='true']` carries
 * `pointer-events: none`, the only control on the app's only unauthenticated screen would be
 * permanently inert with nothing on screen suggesting a reload. The `pageshow` handler cannot
 * help either: there was no cross-document navigation to come back from.
 *
 * Generous on purpose — a successful redirect unloads the page long before this fires, so the
 * timeout is only ever observed in the failure case.
 */
const REDIRECT_TIMEOUT_MS = 10_000;

export function SignInButton() {
  const { signIn } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Re-entry guard as a ref, not state — two clicks in the same tick would both observe a
  // stale `isSigningIn === false` and mint two PKCE verifiers.
  const inFlight = useRef(false);
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const unlatch = useCallback((message: string | null) => {
    if (redirectTimer.current !== null) {
      clearTimeout(redirectTimer.current);
      redirectTimer.current = null;
    }
    inFlight.current = false;
    setIsSigningIn(false);
    setError(message);
  }, []);

  useEffect(
    () => () => {
      if (redirectTimer.current !== null) clearTimeout(redirectTimer.current);
    },
    [],
  );

  // NOTE: there is deliberately no separate aria-live status region here. The pending state is
  // announced by the button's own accessible name changing to "Signing in…" (a valid mechanism
  // for WCAG 4.1.3) together with aria-disabled. Mirroring that same string into a polite live
  // region risks a double announcement on AT that reports name changes for the focused element,
  // and the visible label change is required by the design, so the region is the half that goes.

  // `signIn()` navigates away, so the latch is normally never cleared. A bfcache restore
  // breaks that assumption: the user clicks Sign in, changes their mind at the hosted UI, and
  // presses Back onto a restored page still holding the latch. Because
  // `.btn[aria-disabled='true']` sets `pointer-events: none`, the only control on the app's
  // only unauthenticated screen would be permanently inert, recoverable solely by a manual
  // reload with nothing on screen saying so.
  useEffect(() => {
    function handlePageShow(event: PageTransitionEvent) {
      if (!event.persisted) return;
      // Clears the error too: a restored page would otherwise show a stale failure banner above a
      // freshly-armed button, with nothing having just failed.
      unlatch(null);
    }
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [unlatch]);

  const handleClick = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    setIsSigningIn(true);
    setError(null);

    // Backstop for a redirect that never commits — see REDIRECT_TIMEOUT_MS.
    redirectTimer.current = setTimeout(() => {
      console.error(`Sign-in redirect did not start within ${REDIRECT_TIMEOUT_MS}ms`);
      unlatch(SIGN_IN_ERROR);
    }, REDIRECT_TIMEOUT_MS);

    // One-way latch: deliberately no success branch. `signIn()` resolves immediately after
    // `window.location.assign()` — it does not wait for navigation — so clearing the latch
    // here would flip the label back to "Sign in" while the browser is still tearing down the
    // page. It clears only on failure: this catch, the timeout above, or the bfcache handler.
    signIn().catch((err: unknown) => {
      console.error('Sign-in failed to start', err);
      unlatch(SIGN_IN_ERROR);
    });
  }, [signIn, unlatch]);

  return (
    <>
      {error !== null && (
        <div className="auth-error" role="alert">
          <WarningIcon className="auth-error__icon" />
          <span>{error}</span>
        </div>
      )}
      {/* aria-disabled rather than disabled: `disabled` drops the button from the tab order,
          so a keyboard user loses their place at the exact moment the label changes. */}
      <Button
        className="signin-btn"
        onClick={handleClick}
        {...(isSigningIn ? { 'aria-disabled': true } : {})}
      >
        {isSigningIn ? PENDING_LABEL : IDLE_LABEL}
        <ArrowRightIcon />
      </Button>
    </>
  );
}
